/**
 * Central auth module — single source of truth for token management.
 * All axios instances (old service files + api/client.js) use this.
 */
import axios from "axios";
import env from "../config/env";
import { getOAuthHeaders } from "../constants/oAuthValidation";
import useSessionStore from "../stores/useSessionStore";
import { trace } from "../utils/debugTrace";
import { endPosSession, updatePosSessionTokens } from "../utils/posSession";

// --- Cookie/token helpers ---

export const parseCookies = () => {
  const cookies = {};
  document.cookie.split("; ").forEach((c) => {
    const [name, ...rest] = c.split("=");
    cookies[name] = rest.join("=");
  });
  return cookies;
};

// BUG-2594: the same login every request uses (getOAuthHeaders), not the raw cookie.
export const getAccessToken = () => {
  const token = getOAuthHeaders()?.access_token;
  return token ? token.replace(/^bearer /i, "") : null;
};

// Clears every cookie that feeds getOAuthHeaders()/getAccessToken()'s identity
// checks (accessToken, LastAuthUser, username, Enterprise, userId) — the same
// name patterns those functions match on, so a cleared identity here is a
// cleared identity everywhere the app checks "is there a session". Left
// unmatched: tenant/service-URL cookies (__cw_*), which are CW Browser
// shell-owned and unrelated to the user session.
const AUTH_COOKIE_PATTERNS = ["accessToken", "LastAuthUser", "username", "Enterprise", "userId"];

export const clearAuthCookies = () => {
  // BUG-2594: a logout ends the POS's own login too, not only the shared cookies.
  endPosSession();
  const cookies = parseCookies();
  const hostname = window.location.hostname;
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
  const secure = isLocalhost ? "" : "secure=true;";

  // BUG-2026: a single env.domain guess misses a cookie that was actually set
  // with no domain attribute, or with a different tenant's domain (multi-server
  // switch — e.g. OFC vs KWC each resolve env.domain differently). A cookie
  // deletion only matches if domain+path are identical to how it was set, so a
  // missed variant leaves the old accessToken/username cookie alive. SignIn's
  // SSO-bootstrap check (Authentication/index.js `check()`) then reads that
  // leftover cookie as a valid session and bounces the user back in — which is
  // exactly what left Username/Password stuck non-interactive on "Switch
  // Organization". Try every variant a cookie could plausibly have been set
  // with (mirrors TenantSwitcher.js's domainVariants, the one call site that
  // already got this right).
  const domainVariants = [""];
  if (!isLocalhost) {
    if (env.domain) domainVariants.push(env.domain);
    domainVariants.push(hostname);
    const parts = hostname.split(".");
    if (parts.length > 2) domainVariants.push(parts.slice(-2).join("."));
  }

  Object.keys(cookies).forEach((name) => {
    if (AUTH_COOKIE_PATTERNS.some((p) => name.includes(p))) {
      domainVariants.forEach((domain) => {
        const domainPart = domain ? `domain=${domain};` : "";
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;${secure}SameSite=Strict;${domainPart}`;
      });
    }
  });
};

const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};

export const isTokenExpired = (token) => {
  const decoded = parseJwt(token);
  if (!decoded) return true;
  return decoded.exp * 1000 < Date.now();
};

const setCookie = (name, value, maxAgeSeconds) => {
  let maxAge = maxAgeSeconds;
  if (maxAge == null) {
    const decoded = parseJwt(value);
    maxAge = decoded ? Math.max(decoded.exp - Math.floor(Date.now() / 1000), 0) : 3600;
  }
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const domain = !isLocalhost && env.domain ? `domain=${env.domain};` : "";
  const secure = isLocalhost ? "" : "secure=true;";
  document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; ${secure} SameSite=Strict; ${domain}`;
};

// --- Token refresh (promise-deduplicated) ---

let refreshPromise = null;

// A caller already sitting on /sign-in (typing credentials, or bounced there
// moments ago by another failed call) gets nothing from a second hard
// navigation to the same place — it just interrupts them and refetches every
// asset. Only force the trip when it would actually go somewhere new.
// BUG-2026: must await the shell's logout IPC before navigating — it's the
// authoritative teardown (clears cookies across every webview partition, not
// just this document's). Firing it and navigating immediately raced the
// teardown; navigating away could also tear down the listener the shell uses
// to react to the logout event, so it never got applied.
//
// SIGNIN-REDIRECT-LOOP: this used to navigate to /sign-in WITHOUT clearing this document's
// own accessToken/username/etc cookies first. electronAPI.auth.logout() is a
// no-op in any non-Electron deployment (plain browser / installed PWA — where
// pos2.* actually runs), so those cookies survived the trip untouched.
// Authentication/index.js's check() then read the still-present accessToken
// cookie on the very next /sign-in load as an active CW Browser SSO session
// and hard-redirected straight back into the app — which failed the same way
// again, calling forceSignIn again, forever. clearAuthCookies() here (the same
// helper useAuthStore.logout() already uses) makes the sign-out this function
// claims to perform actually complete, on every deployment mode, so /sign-in
// has nothing left to auto-bootstrap from.
// BUG-2066/2067: every lock screen (SuperMarket, Production, ProductionRMIssue,
// PreOrder, Estimate, Kiosk) sets useSessionStore's `locked` flag synchronously,
// in the same tick as clearing cookies and hard-navigating to its own
// /sign-in?username=...&readOnly=true. Because JS is single-threaded, any
// pending request's 401 can only reach this interceptor *after* that whole
// synchronous block — including setLocked(true) — has already run. So a 401
// landing here while `locked` is true is never a real session failure, it's
// this same lock-relogin race. Escalating anyway used to call
// electronAPI.auth.logout() and navigate to a bare /sign-in with no
// readOnly=true — the CW Browser shell (electron/main.js `checkNav`) treats
// exactly that combination as a genuine logout and tears down the *entire*
// application's session (authBus.onWebviewLogout → clearAuthState), not just
// this webview's. That's what sent the user to the shell's own generic
// sign-in screen (org switcher, blank username) instead of the POS-scoped,
// prefilled one the lock screen was already navigating to. Bailing out here
// lets that already-in-flight, correctly-scoped navigation win instead.
const forceSignIn = async () => {
  if (useSessionStore.getState().locked) {
    trace("AuthInterceptor", "forceSignIn bailed — locked");
    return;
  }
  clearAuthCookies();
  localStorage.removeItem("refreshToken");
  await window.electronAPI?.auth?.logout();
  trace("AuthInterceptor", "forceSignIn proceeded", { pathname: window.location.pathname });
  if (window.location.pathname !== "/sign-in") {
    trace("AuthInterceptor", "forceSignIn navigating to bare /sign-in", { fromPathname: window.location.pathname });
    window.location.assign("/sign-in");
  }
};

const performRefresh = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken || !env.refreshTokenUrl) {
    trace("AuthInterceptor", "performRefresh — no refreshToken/refreshTokenUrl, forcing sign-in", {
      hasRefreshToken: !!refreshToken,
      hasRefreshTokenUrl: !!env.refreshTokenUrl,
    });
    await forceSignIn();
    return null;
  }

  try {
    const res = await axios.post(
      env.refreshTokenUrl,
      { refreshToken },
      { headers: { "Content-Type": "application/json" } }
    );

    if (res.status === 200 && res.data.accessToken) {
      // BUG-2594: the POS's own login follows its own refresh, before the cookies change.
      updatePosSessionTokens({ accessToken: res.data.accessToken, refreshToken: res.data.refreshToken });
      setCookie("accessToken", res.data.accessToken);
      setCookie("Enterprise.accessToken", res.data.accessToken);
      setCookie("Enterprise.refreshToken", res.data.refreshToken);
      // Propagate refreshed token to all webview partitions via authBus
      window.electronAPI?.auth?.tokenRefreshed({
        accessToken: res.data.accessToken,
        refreshToken: res.data.refreshToken,
      });
      return res.data.accessToken;
    }

    trace("AuthInterceptor", "performRefresh — refresh response not OK, forcing sign-in", {
      status: res.status,
      hasAccessToken: !!res.data?.accessToken,
    });
    await forceSignIn();
    return null;
  } catch (err) {
    trace("AuthInterceptor", "performRefresh — refresh request threw, forcing sign-in", {
      error: err?.message,
    });
    await forceSignIn();
    return null;
  }
};

export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  // .finally's callback is always scheduled as a microtask, even on a promise
  // that's already settled by the time it's attached — unlike a `finally`
  // block written inside performRefresh itself, which (on the fully
  // synchronous "no refreshToken" path, no await ever hit) would run BEFORE
  // this assignment completes and reset refreshPromise to null one line
  // before it gets overwritten back to a non-null promise. That left the
  // dedup guard permanently stuck "on" after the very first no-token call —
  // every later refresh attempt on the same page, even after a fresh login
  // supplied a real refreshToken, silently short-circuited to the stale
  // cached null instead of ever trying again.
  refreshPromise = performRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

// --- Interceptors ---

/**
 * Request interceptor: attach current access token to outgoing requests.
 */
const attachAuthToken = (config) => {
  const authHeaders = getOAuthHeaders() || {};
  const { access_token } = authHeaders;
  if (access_token) {
    config.headers.Authorization = /^bearer /i.test(access_token) ? access_token : `Bearer ${access_token}`;
  }
  return config;
};

/**
 * Response interceptor: on 401, refresh token and retry the failed request once.
 * _retried flag prevents infinite retry loops.
 */
const handleAuthError = async (error) => {
  const status = error?.response?.status;
  const msg = error?.message || "";

  // SIGNIN-REDIRECT-LOOP: "Unexpected token <" alone (without an actual 401 status) means
  // the response body was HTML, not JSON — e.g. a misrouted URL hitting an
  // nginx 404/502 page, not a session problem. Treating that as an auth
  // failure forced an unnecessary sign-out (and, combined with the forceSignIn
  // cookie gap above, could re-trigger the same misrouted call again and loop)
  // over what was actually a URL/config problem. A genuine 401 whose body
  // happens to be HTML is still caught by the status check below either way.
  if (status === 401 || msg.includes("status code 401")) {
    trace("AuthInterceptor", "401 received", {
      url: error.config?.url,
      method: error.config?.method,
      alreadyRetried: !!error.config?._retried,
      locked: useSessionStore.getState().locked,
    });
    if (!error.config?._retried) {
      const newToken = await refreshAccessToken();
      if (newToken && error.config) {
        error.config._retried = true;
        error.config.headers.Authorization = `Bearer ${newToken}`;
        return axios(error.config);
      }
    }
  }
  return Promise.reject(error);
};

/**
 * Apply standard auth interceptors to an axios instance.
 * Call this after any tenant baseURL interceptors.
 */
export const applyAuthInterceptors = (instance) => {
  instance.interceptors.request.use(attachAuthToken, (err) => Promise.reject(err));
  instance.interceptors.response.use((res) => res, handleAuthError);
};
